import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { parseLocalDate } from '../utils/dates';
import { useFacility } from '../contexts/FacilityContext';
import { fetchWorklist, submitClaimsToClaimMd, syncClaimMdStatus, voidClaim, createManualEncounter, fetchPatients, generateClaimMdEdi, updateEncounter, fetchProviders, fetchPayers } from '../services/api';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import ReviewClaimModal from '../components/ReviewClaimModal';
import EdiPreviewModal from '../components/EdiPreviewModal';
import ProviderTypeahead from '../components/ProviderTypeahead';
import {
  facilityDefaultForm,
  resolveFacilityComponent,
  claimFormLabel,
  requiredEncounterProvider,
} from '../utils/billing';
import './Worklist.css';

const NOTE_TYPE_CONFIG = {
  clinic:    { label: 'Clinic',    className: 'wl-note-clinic'    },
  procedure: { label: 'Procedure', className: 'wl-note-procedure' },
  nursing:   { label: 'Nursing',   className: 'wl-note-nursing'   },
};

const CLAIM_STATUS_CONFIG = {
  ready:     { label: 'Ready',     className: 'wl-status-ready'     },
  generated: { label: 'Generated', className: 'wl-status-generated' },
  pending:   { label: 'Pending',   className: 'wl-status-pending'   },
  accepted:  { label: 'Accepted',  className: 'wl-status-accepted'  },
  rejected:  { label: 'Rejected',  className: 'wl-status-rejected'  },
  voided:    { label: 'Voided',    className: 'wl-status-voided'    },
  void_pending: { label: 'Void pending', className: 'wl-status-pending' },
  held:      { label: 'Held',      className: 'wl-status-held'      },
};

// ⟳ Plan Step 6 — hold badges. An encounter accepted from the source but not yet
// billable carries one chip per blocking reason, worked on the Holds page.
//
// 🔴 A hold is NOT the same thing as claim_status === 'held'. A revision hold (the
// source sent a changed version of an already-billed encounter) deliberately leaves
// the claim's status alone, so a SUBMITTED row can carry a chip. That is correct —
// read the chips from claim.holds, never from the status column.
const HOLD_REASON_LABELS = {
  claim_form_unresolved: 'No claim form',
  coverage_missing: 'No coverage',
  coverage_termed: 'Coverage ended',
  missing_prior_auth: 'No prior auth',
  unknown_payer: 'Unknown payer',
  missing_required_field: 'Missing info',
  possible_existing_claim: 'Possible duplicate',
  price_mismatch: 'Price mismatch',
  revision_review: 'Source revised',
};

const ACTIONABLE_STATUSES  = ['ready', 'rejected'];

// A SELFPAY claim has no clearinghouse lifecycle — generating the CMS-1500/1450 form is
// the terminal billing action here. Once generated it drops off the worklist queues (still
// reachable from the patient summary). The is_self_pay flag is set by the worklist query
// only when self-pay is the patient's sole/primary coverage, so this never hides a claim
// that still has a real downstream payer to bill.
const isSelfPayGenerated = (c) => c.is_self_pay && c.claim_status === 'generated';

// A synced claim is known at the facility by its source's own id: the RIS's order id,
// the EMR's appointment id. "Order 10" is what the facility and its messages say.
const SOURCE_ENCOUNTER_NOUN = { ris: 'Order' };
const sourceEncounterLabel = (c) =>
  `${SOURCE_ENCOUNTER_NOUN[c.source_system] ?? 'Visit'} ${c.source_encounter_id}`;

const formatUsd = (value) =>
  `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// The Amount cell. `amount` is the sum of the coded service lines, so a claim nobody has
// coded yet reads 0 whether or not its price is known. Before coding, a source-priced
// self-pay claim shows the total /generate will require (self_pay_source_charge, which
// may legitimately be 0.00); any other uncoded claim has no amount yet and shows a dash.
// A non-zero amount always shows, so a claim stored without lines never hides its total.
function AmountCell({ claim }) {
  if (claim.has_service_lines || claim.amount !== 0) {
    return <td className="wl-cell-amount">{formatUsd(claim.amount)}</td>;
  }
  if (claim.self_pay_source_charge != null) {
    return (
      <td className="wl-cell-amount">
        {formatUsd(claim.self_pay_source_charge)}
        <span
          className="wl-amount-source"
          title="The facility's self-pay price. The claim must total exactly this when it is generated."
        >
          Source price
        </span>
      </td>
    );
  }
  return (
    <td className="wl-cell-amount">
      <span className="wl-amount-none" title="Not coded yet. The amount comes from the service lines.">—</span>
    </td>
  );
}

function Worklist() {
  const { selectedFacility } = useFacility();
  const navigate = useNavigate();

  const [filterStatus, setFilterStatus]   = useState('needs-attention');
  const [searchQuery, setSearchQuery]     = useState('');
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [selectedClaims, setSelectedClaims] = useState(new Set());
  const [submitting, setSubmitting]       = useState(false);
  const [generatingEdi, setGeneratingEdi] = useState(false);
  const [ediPreview, setEdiPreview]       = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [voidModal, setVoidModal]         = useState(null);
  const [voidReason, setVoidReason]       = useState('');
  const [voiding, setVoiding]             = useState(false);
  const [reviewModal, setReviewModal]     = useState(null);

  // Create claim modal state
  const [createModal, setCreateModal]           = useState(false);
  const [creating, setCreating]                 = useState(false);
  const [createError, setCreateError]           = useState(null);
  const [patientMode, setPatientMode]           = useState('existing');
  const [patientList, setPatientList]           = useState([]);
  const [patientSearch, setPatientSearch]       = useState('');
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const [patientsLoading, setPatientsLoading]   = useState(false);
  const [providerList, setProviderList]         = useState([]);
  const [createForm, setCreateForm]             = useState({
    patientId: '', firstName: '', lastName: '', dob: '', sex: 'U',
    phone: '', email: '', streetAddress: '', city: '', state: '', zip: '',
    mrn: '',
    serviceDate: '', renderingProvider: '', renderingProviderNPI: '',
    referringProvider: '', referringProviderNPI: '', formType: 'CMS-1500',
    payerId: null, payerName: '', policyNumber: '', effectiveDate: '', isSelfPayPayer: false,
  });

  // Payer state for Create Claim modal
  const [selectedPatientHasPayer, setSelectedPatientHasPayer] = useState(null);
  const [createPayersList, setCreatePayersList]               = useState([]);
  const [createPayerSearch, setCreatePayerSearch]             = useState('');
  const [createPayerDropdownOpen, setCreatePayerDropdownOpen] = useState(false);

  // Edit encounter modal state
  const [editEncounterModal, setEditEncounterModal] = useState(null);
  const [editEncounterForm, setEditEncounterForm]   = useState({});
  const [savingEncounter, setSavingEncounter]       = useState(false);
  const [editEncounterError, setEditEncounterError] = useState(null);

  // Link provider modal state
  const [linkProviderModal, setLinkProviderModal]     = useState(null);
  const [linkProviderForm, setLinkProviderForm]       = useState({});
  const [savingLinkProvider, setSavingLinkProvider]   = useState(false);
  const [linkProviderError, setLinkProviderError]     = useState(null);

  // Data state
  const [allClaims, setAllClaims] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const loadWorklist = useCallback(async () => {
    if (!selectedFacility) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWorklist(selectedFacility.id);
      setAllClaims(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load worklist');
    } finally {
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    loadWorklist();
  }, [loadWorklist]);

  useEffect(() => {
    fetchProviders().then((res) => setProviderList(res.data || [])).catch(() => {});
  }, []);

  const loadPatients = useCallback(async () => {
    if (!selectedFacility) return;
    setPatientsLoading(true);
    try {
      const res = await fetchPatients(selectedFacility.id);
      setPatientList(res.data || []);
    } catch {
      // non-fatal — dropdown will be empty
    } finally {
      setPatientsLoading(false);
    }
  }, [selectedFacility]);

  const filteredPatients = useMemo(() => {
    if (!patientSearch.trim()) return patientList;
    const q = patientSearch.toLowerCase();
    return patientList.filter((p) =>
      `${p.first_name} ${p.last_name}`.toLowerCase().includes(q) ||
      (p.date_of_birth || '').includes(q)
    );
  }, [patientList, patientSearch]);

  const filteredClaims = useMemo(() => {
    // Generated self-pay claims pop off every worklist queue — see isSelfPayGenerated.
    let result = allClaims.filter((c) => !isSelfPayGenerated(c));

    if (filterStatus === 'needs-attention') {
      result = result.filter((c) => ACTIONABLE_STATUSES.includes(c.claim_status));
    } else if (filterStatus === 'no-provider-link') {
      result = result.filter((c) => c.provider && !c.rendering_provider_id);
    } else if (filterStatus === 'technical') {
      // Split-billed encounters, across every status — a technical claim is otherwise
      // indistinguishable from a professional one in this table.
      result = result.filter((c) => c.billing_component === 'technical');
    } else if (filterStatus !== 'all') {
      result = result.filter((c) => c.claim_status === filterStatus);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          (c.patient_name  || '').toLowerCase().includes(q) ||
          (c.encounter_id  || '').toLowerCase().includes(q) ||
          (c.source_encounter_id ? sourceEncounterLabel(c).toLowerCase().includes(q) : false) ||
          (c.claim_number  || '').toLowerCase().includes(q) ||
          (c.provider      || '').toLowerCase().includes(q) ||
          (c.referring_provider || '').toLowerCase().includes(q) ||
          (c.provisional_procedure_code || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [allClaims, filterStatus, searchQuery]);

  const statusCounts = useMemo(() => {
    const counts = { ready: 0, generated: 0, rejected: 0, voided: 0 };
    allClaims.forEach((c) => {
      if (isSelfPayGenerated(c)) return; // popped off — excluded from every queue count
      if (counts[c.claim_status] !== undefined) counts[c.claim_status]++;
    });
    counts.needsAttention  = counts.ready + counts.rejected;
    counts.noProviderLink  = allClaims.filter((c) => c.provider && !c.rendering_provider_id && !isSelfPayGenerated(c)).length;
    counts.technical       = allClaims.filter((c) => c.billing_component === 'technical' && !isSelfPayGenerated(c)).length;
    return counts;
  }, [allClaims]);

  // Which provider an encounter must name depends on what it bills. A technical facility
  // bills the TC half of a diagnostic test — nobody renders it — so the ORDERING physician is
  // the required one (837P loop 2310A / box 17) and the rendering slot stays empty. Mirrors
  // missingEncounterProvider() server-side, which is what POST /worklist/manual enforces;
  // this gate used to demand a rendering provider unconditionally, so a technical facility
  // could not create an encounter at all.
  const facilityComponent = resolveFacilityComponent(selectedFacility);
  const referringRequired = requiredEncounterProvider(facilityComponent) === 'referring';

  const createFormValid =
    createForm.serviceDate &&
    (referringRequired
      ? createForm.referringProvider.trim()
      : createForm.renderingProvider.trim()) &&
    (patientMode === 'existing'
      ? !!createForm.patientId && selectedPatientHasPayer === true
      : createForm.firstName.trim() && createForm.lastName.trim() && createForm.dob &&
        !!createForm.payerId && !!createForm.effectiveDate &&
        (createForm.isSelfPayPayer || !!createForm.policyNumber.trim()));

  const showCheckboxes       = filterStatus === 'generated';
  const needsAttentionActive = filterStatus === 'needs-attention';
  const generatedInView        = filteredClaims.filter((c) => c.claim_status === 'generated');
  const allGeneratedSelected   =
    generatedInView.length > 0 && generatedInView.every((c) => selectedClaims.has(c.id));

  const toggleClaimSelection = (claimId) => {
    setSelectedClaims((prev) => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId);
      else next.add(claimId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allGeneratedSelected) {
      setSelectedClaims(new Set());
    } else {
      setSelectedClaims(new Set(generatedInView.map((c) => c.id)));
    }
  };

  const handleBatchSubmit = async (claimIds) => {
    if (claimIds.length === 0) return;

    const count     = claimIds.length;
    const confirmed = window.confirm(
      `Submit ${count} claim${count > 1 ? 's' : ''} to Claim.MD?\n\n` +
      `This transmits the 837 claim file(s) to the clearinghouse. Accepted claims ` +
      `move to Pending; any rejected claims are flagged so you can correct and resubmit.`
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const res      = await submitClaimsToClaimMd(claimIds);
      const batches  = res?.batches || [];
      const accepted = batches.reduce((s, b) => s + (b.accepted || 0), 0);
      const rejected = batches.reduce((s, b) => s + (b.rejected || 0), 0);
      const rejectedClaims = batches.flatMap((b) =>
        (b.claims || []).filter((c) => /^(R|E)/i.test(c.status || ''))
      );

      let msg = `Submitted ${res?.claimCount ?? count} claim(s) to Claim.MD.\n\n`
              + `✓ Accepted: ${accepted}\n✗ Rejected: ${rejected}`;
      if (rejectedClaims.length > 0) {
        msg += '\n\nRejections to correct:\n'
             + rejectedClaims.map((c) => `• ${c.claim_number}: ${c.message || 'rejected'}`).join('\n');
      }
      // Non-blocking component findings — a claim can be accepted by the clearinghouse and
      // still be billed wrong (a technical line left at the global price is the common one).
      const vw = res?.validationWarnings || [];
      if (vw.length > 0) {
        msg += '\n\nReview these — they did not block submission:\n'
             + vw.map((w) => `• ${w.claimId}: ${w.warnings.join('; ')}`).join('\n');
      }
      window.alert(msg);
    } catch (err) {
      // 837 readiness validation runs before anything is transmitted.
      const ve = err?.response?.data?.validationErrors;
      if (ve) {
        window.alert(
          'Cannot submit — fix these before sending:\n\n'
          + ve.map((e) => `• ${e.claimId}: ${e.errors.join('; ')}`).join('\n')
        );
      } else {
        window.alert('Submission failed: ' + (err?.response?.data?.message || err.message));
      }
    } finally {
      setSelectedClaims(new Set());
      setSubmitting(false);
      await loadWorklist();
    }
  };

  const handleBatchGenerateEdi = async (claimIds) => {
    if (claimIds.length === 0) return;
    setGeneratingEdi(true);
    try {
      console.log('[EDI] batch generating for claimIds:', claimIds);
      const res = await generateClaimMdEdi(claimIds);
      console.log('[EDI] batch response:', res);
      const files = res?.files || [];
      console.log('[EDI] batch files count:', files.length);
      if (files.length > 0) setEdiPreview(files);
      else console.warn('[EDI] files array is empty — modal will not open');
    } catch (err) {
      console.error('EDI generation error:', err);
    } finally {
      setGeneratingEdi(false);
    }
  };

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      // Claim.MD's response feed is account-wide (not per-facility) — it pulls
      // every status update since the last poll and applies them to claims.
      const res = await syncClaimMdStatus();
      await loadWorklist();
      window.alert(
        `Claim.MD status sync complete.\n\n`
        + `Responses processed: ${res?.processed ?? 0}\n`
        + `Claims updated: ${res?.matched ?? 0}`
      );
    } catch (err) {
      window.alert('Status check failed: ' + (err?.response?.data?.message || err.message));
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleVoidClaim = async () => {
    if (!voidModal || !voidReason.trim()) return;
    setVoiding(true);
    try {
      await voidClaim(voidModal.id, voidReason.trim());
      setVoidModal(null);
      setVoidReason('');
      await loadWorklist();
    } catch (err) {
      console.error('Void error:', err.message);
    } finally {
      setVoiding(false);
    }
  };

  const openEditEncounterModal = (claim) => {
    setEditEncounterForm({
      serviceDate:          claim.service_date        ? String(claim.service_date).slice(0, 10) : '',
      formType:             facilityDefaultForm(selectedFacility?.billingType),
      // The CLAIM's component, not the facility's — a claim may have been re-componented on
      // the form after it was created.
      billingComponent:     claim.billing_component || resolveFacilityComponent(selectedFacility),
      renderingProvider:    claim.provider            || '',
      renderingProviderNPI: claim.rendering_provider_npi || '',
      renderingProviderId:  claim.rendering_provider_id  || null,
      referringProvider:    claim.referring_provider  || '',
      referringProviderNPI: claim.referring_provider_npi || '',
    });
    setEditEncounterError(null);
    setEditEncounterModal(claim);
  };

  const openLinkProviderModal = (claim) => {
    setLinkProviderForm({
      serviceDate:          claim.service_date           ? String(claim.service_date).slice(0, 10) : '',
      formType:             facilityDefaultForm(selectedFacility?.billingType),
      renderingProvider:    claim.provider               || '',
      renderingProviderNPI: claim.rendering_provider_npi || '',
      renderingProviderId:  claim.rendering_provider_id  || null,
      referringProvider:    claim.referring_provider     || '',
      referringProviderNPI: claim.referring_provider_npi || '',
    });
    setLinkProviderError(null);
    setLinkProviderModal(claim);
  };

  const handleSaveLinkProvider = async () => {
    if (!linkProviderForm.renderingProvider?.trim()) {
      setLinkProviderError('Rendering provider is required.');
      return;
    }
    setSavingLinkProvider(true);
    setLinkProviderError(null);
    try {
      await updateEncounter(linkProviderModal.id, {
        service_date:           linkProviderForm.serviceDate,
        form_type:              linkProviderForm.formType,
        rendering_provider:     linkProviderForm.renderingProvider.trim(),
        rendering_provider_npi: linkProviderForm.renderingProviderNPI.trim() || null,
        rendering_provider_id:  linkProviderForm.renderingProviderId         || null,
        referring_provider:     linkProviderForm.referringProvider.trim()    || null,
        referring_provider_npi: linkProviderForm.referringProviderNPI.trim() || null,
      });
      setLinkProviderModal(null);
      await loadWorklist();
    } catch (err) {
      setLinkProviderError(err?.response?.data?.error || 'Failed to update provider link.');
    } finally {
      setSavingLinkProvider(false);
    }
  };

  const handleSaveEncounter = async () => {
    // Same rule as the create modal and PATCH /worklist/:id/encounter: a technical claim
    // names the ordering provider, everything else names a rendering one.
    if (requiredEncounterProvider(editEncounterForm.billingComponent) === 'referring') {
      if (!editEncounterForm.referringProvider?.trim()) {
        setEditEncounterError('Referring (ordering) provider is required on a technical-component claim.');
        return;
      }
    } else if (!editEncounterForm.renderingProvider?.trim()) {
      setEditEncounterError('Rendering provider is required.');
      return;
    }
    if (!editEncounterForm.serviceDate) {
      setEditEncounterError('Service date is required.');
      return;
    }
    setSavingEncounter(true);
    setEditEncounterError(null);
    try {
      await updateEncounter(editEncounterModal.id, {
        service_date:           editEncounterForm.serviceDate,
        form_type:              editEncounterForm.formType,
        rendering_provider:     editEncounterForm.renderingProvider.trim(),
        rendering_provider_npi: editEncounterForm.renderingProviderNPI.trim() || null,
        rendering_provider_id:  editEncounterForm.renderingProviderId         || null,
        referring_provider:     editEncounterForm.referringProvider.trim()    || null,
        referring_provider_npi: editEncounterForm.referringProviderNPI.trim() || null,
      });
      setEditEncounterModal(null);
      await loadWorklist();
    } catch (err) {
      setEditEncounterError(err?.response?.data?.error || 'Failed to update encounter.');
    } finally {
      setSavingEncounter(false);
    }
  };

  const handleAction = (action, claim) => {
    setActionMenuOpen(null);
    if (action === 'edit-encounter') {
      openEditEncounterModal(claim);
      return;
    }
    if (action === 'generate') {
      navigate('/worklist/generate', { state: { claim } });
      return;
    }
    if (action === 'review') {
      setReviewModal(claim);
      return;
    }
    if (action === 'view') {
      navigate(`/claims/${claim.id}`);
      return;
    }
    if (action === 'edit') {
      navigate('/worklist/generate', { state: { claim, isEdit: true } });
      return;
    }
    if (action === 'resubmit') {
      setReviewModal(claim);
      return;
    }
    if (action === 'void') {
      setVoidModal(claim);
      setVoidReason('');
      return;
    }
  };

  const openCreateModal = () => {
    setPatientMode('existing');
    setPatientSearch('');
    setPatientSearchOpen(false);
    setCreateError(null);
    setSelectedPatientHasPayer(null);
    setCreatePayerSearch('');
    setCreatePayerDropdownOpen(false);
    setCreateForm({
      patientId: '', firstName: '', lastName: '', dob: '', sex: 'U',
      phone: '', email: '', streetAddress: '', city: '', state: '', zip: '',
      mrn: '',
      serviceDate: '',
      renderingProvider: '',
      renderingProviderNPI: '',
      renderingProviderId: null,
      referringProvider: '',
      referringProviderNPI: '',
      formType: facilityDefaultForm(selectedFacility?.billingType),
      payerId: null, payerName: '', policyNumber: '', effectiveDate: '', isSelfPayPayer: false,
    });
    setCreateModal(true);
    loadPatients();
    fetchPayers().then((res) => setCreatePayersList(res.data || [])).catch(() => {});
  };

  const handleCreateClaim = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      const body = {
        facility_id:            selectedFacility.id,
        service_date:           createForm.serviceDate,
        rendering_provider:     createForm.renderingProvider,
        rendering_provider_npi: createForm.renderingProviderNPI || undefined,
        rendering_provider_id:  createForm.renderingProviderId  || undefined,
        referring_provider:     createForm.referringProvider    || undefined,
        referring_provider_npi: createForm.referringProviderNPI || undefined,
        form_type:              createForm.formType,
        mrn:                    createForm.mrn?.trim()          || undefined,
      };
      if (patientMode === 'existing') {
        body.patient_id = createForm.patientId;
      } else {
        body.patient = {
          first_name:     createForm.firstName,
          last_name:      createForm.lastName,
          date_of_birth:  createForm.dob,
          sex:            createForm.sex,
          phone:          createForm.phone          || null,
          email:          createForm.email          || null,
          street_address: createForm.streetAddress  || '',
          city:           createForm.city           || '',
          state:          createForm.state          || '',
          zip:            createForm.zip            || '',
        };
        body.payer_id        = createForm.payerId       || undefined;
        body.policy_number   = createForm.policyNumber  || undefined;
        body.effective_date  = createForm.effectiveDate || undefined;
      }
      await createManualEncounter(body);
      setCreateModal(false);
      await loadWorklist();
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create claim');
    } finally {
      setCreating(false);
    }
  };

  if (!selectedFacility) {
    return (
      <div className="wl-page">
        <div className="wl-no-facility">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
          <h2>No Facility Selected</h2>
          <p>Select a facility to view its claims worklist.</p>
          <Link to="/" className="wl-select-btn">Select a Facility</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="wl-page">
      <ActiveFacilityBanner />

      {/* Header */}
      <div className="wl-header">
        <div className="wl-header-text">
          <h1 className="wl-title">Worklist</h1>
          <p className="wl-subtitle">
            {statusCounts.needsAttention > 0
              ? `${statusCounts.needsAttention} claim${statusCounts.needsAttention !== 1 ? 's' : ''} need${statusCounts.needsAttention === 1 ? 's' : ''} attention · ${selectedFacility.name}`
              : `All caught up · ${selectedFacility.name}`}
          </p>
        </div>
        <div className="wl-header-actions">
          <button className="wl-btn-create" onClick={openCreateModal}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Create Claim
          </button>
          <button
            className="wl-btn-check-status"
            onClick={handleCheckStatus}
            disabled={checkingStatus}
            title="Poll Claim.MD for delayed claim-status updates"
          >
            {checkingStatus ? (
              <>
                <span className="wl-spinner" />
                Checking...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                </svg>
                Check Status
              </>
            )}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="wl-toolbar">
        <div className="wl-filters">
          <button
            className={`wl-filter-chip wl-chip-needs-attention ${needsAttentionActive ? 'active' : ''}`}
            onClick={() => { setFilterStatus('needs-attention'); setSelectedClaims(new Set()); }}
          >
            Needs Attention
            <span className="wl-chip-count">{statusCounts.needsAttention}</span>
          </button>

          {['ready', 'generated'].map((status) => (
            <button
              key={status}
              className={`wl-filter-chip wl-chip-${status} ${filterStatus === status ? 'active' : ''}`}
              onClick={() => { setFilterStatus(status); setSelectedClaims(new Set()); }}
            >
              {CLAIM_STATUS_CONFIG[status].label}
              <span className="wl-chip-count">{statusCounts[status]}</span>
            </button>
          ))}

          <button
            className={`wl-filter-chip wl-chip-rejected ${filterStatus === 'rejected' ? 'active' : ''}`}
            onClick={() => { setFilterStatus('rejected'); setSelectedClaims(new Set()); }}
          >
            Rejected
            <span className="wl-chip-count">{statusCounts.rejected}</span>
          </button>

          <button
            className={`wl-filter-chip ${filterStatus === 'voided' ? 'active' : ''} wl-chip-voided`}
            onClick={() => { setFilterStatus('voided'); setSelectedClaims(new Set()); }}
          >
            Voided <span className="wl-chip-count">{statusCounts.voided}</span>
          </button>

          {/* Shown only once the facility actually bills TC — same conditional pattern as the
              provider-link chip, so a center that never split-bills sees no extra chrome. */}
          {statusCounts.technical > 0 && (
            <button
              className={`wl-filter-chip wl-chip-technical ${filterStatus === 'technical' ? 'active' : ''}`}
              onClick={() => { setFilterStatus('technical'); setSelectedClaims(new Set()); }}
              title="Encounters billed as the technical component only (modifier TC)"
            >
              Technical
              <span className="wl-chip-count">{statusCounts.technical}</span>
            </button>
          )}

          {statusCounts.noProviderLink > 0 && (
            <button
              className={`wl-filter-chip wl-chip-no-provider ${filterStatus === 'no-provider-link' ? 'active' : ''}`}
              onClick={() => { setFilterStatus('no-provider-link'); setSelectedClaims(new Set()); }}
              title="Claims with a provider name but no link to the provider master list"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                <line x1="2" y1="2" x2="22" y2="22" />
              </svg>
              No Provider Linked
              <span className="wl-chip-count">{statusCounts.noProviderLink}</span>
            </button>
          )}
        </div>

        <div className="wl-search-wrapper">
          <svg className="wl-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="wl-search-input"
            placeholder="Search patient, encounter, provider..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Batch Action Bar */}
      {showCheckboxes && selectedClaims.size > 0 && (
        <div className="wl-batch-bar">
          <div className="wl-batch-info">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>{selectedClaims.size} claim{selectedClaims.size > 1 ? 's' : ''} selected</span>
          </div>
          <div className="wl-batch-actions">
            <button
              className="wl-btn-batch-edi"
              onClick={() => handleBatchGenerateEdi([...selectedClaims])}
              disabled={generatingEdi || submitting}
            >
              {generatingEdi ? (
                <><span className="wl-spinner" />Generating…</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Generate .edi
                </>
              )}
            </button>
            <button
              className="wl-btn-batch-submit"
              onClick={() => handleBatchSubmit([...selectedClaims])}
              disabled={submitting || generatingEdi}
            >
              {submitting ? (
                <><span className="wl-spinner" />Submitting...</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  Submit to Claim.MD
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="wl-empty">
          <p>Loading worklist…</p>
        </div>
      ) : error ? (
        <div className="wl-empty">
          <p style={{ color: '#ef4444' }}>{error}</p>
        </div>
      ) : filteredClaims.length === 0 ? (
        <div className="wl-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="8" y1="15" x2="16" y2="15" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
          <p>{filterStatus === 'needs-attention' ? 'All caught up — no claims need attention.' : 'No encounters match your filters.'}</p>
        </div>
      ) : (
        <div className="wl-table-container">
          <table className="wl-table">
            <thead>
              <tr>
                {showCheckboxes && (
                  <th className="wl-th-checkbox">
                    <label className="wl-checkbox-label">
                      <input type="checkbox" checked={allGeneratedSelected} onChange={toggleSelectAll} />
                      <span className="wl-checkbox-custom" />
                    </label>
                  </th>
                )}
                <th>Encounter</th>
                {/* ⟳ Plan Step 8 — claim PROVENANCE. Separate from the hold badges
                    Step 6 added: this says where the claim CAME FROM, not what is
                    wrong with it. */}
                <th>Source</th>
                <th>Patient</th>
                <th>Provider</th>
                <th>Service Date</th>
                <th>Codes / Note</th>
                <th className="num">Amount</th>
                <th>Status</th>
                <th className="wl-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClaims.map((claim) => {
                const statusConfig = CLAIM_STATUS_CONFIG[claim.claim_status] || CLAIM_STATUS_CONFIG.ready;
                const holds = claim.holds || [];
                return (
                  <tr key={claim.id} className={selectedClaims.has(claim.id) ? 'wl-row-selected' : ''}>
                    {showCheckboxes && (
                      <td className="wl-cell-checkbox">
                        <label className="wl-checkbox-label">
                          <input
                            type="checkbox"
                            checked={selectedClaims.has(claim.id)}
                            onChange={() => toggleClaimSelection(claim.id)}
                          />
                          <span className="wl-checkbox-custom" />
                        </label>
                      </td>
                    )}
                    <td>
                      {claim.source_encounter_id ? (
                        <span className="wl-encounter-id" title={`The ${claim.source_system} system's id for this visit`}>
                          {sourceEncounterLabel(claim)}
                        </span>
                      ) : (
                        <span className="wl-encounter-id">{claim.encounter_id}</span>
                      )}
                      <span className="wl-claim-num">{claim.claim_number}</span>
                      {claim.note_type && NOTE_TYPE_CONFIG[claim.note_type] && (
                        <span className={`wl-note-badge ${NOTE_TYPE_CONFIG[claim.note_type].className}`}>
                          {NOTE_TYPE_CONFIG[claim.note_type].label}
                        </span>
                      )}
                      {claim.is_self_pay && (
                        <span className="wl-note-badge wl-note-selfpay" title="Self-pay — drops off the worklist once the claim form is generated">
                          Self-Pay
                        </span>
                      )}
                      {/* ── 🔴 Plan Step 8 (S8-c) — THE SELF-PAY SOURCE NOTICE ──
                          DISPLAY ONLY. It changes no status, no routing and no payer
                          assignment. Its whole purpose is to stop a biller looking at a
                          COVERED patient's self-pay claim from "correcting" it — the
                          inversion guard arriving through a human instead of through code.

                          ⚠️ It states WHAT THE SOURCE DECLARED and WHERE TO RESOLVE IT.
                          It must never assert that a legal restriction exists: the RCM
                          does not know whether a §164.522 request was made, and a
                          billing-type declaration and a restriction record are different
                          facts. The backend authors this string; do not reword it here. */}
                      {claim.self_pay_source_notice && (
                        <span
                          className="wl-note-badge wl-note-selfpay-source"
                          title={claim.self_pay_source_notice}
                        >
                          Self-Pay per facility
                        </span>
                      )}
                      {claim.is_rebill && (
                        <span className="wl-note-badge wl-note-rebill" title="Rebill — payer was reassigned; review and submit to the new payer">
                          Rebill
                        </span>
                      )}
                      {claim.billing_component === 'technical' && (
                        <span className="wl-note-badge wl-note-technical" title="Technical component only — every line is billed with modifier TC at the technical price; the reading physician bills the 26 separately">
                          Technical
                        </span>
                      )}
                    </td>
                    {/* ⟳ Plan Step 8 — source vs manual. A synced claim's encounter
                        was finalized in the facility's own system; a manual one was
                        keyed here. Naming the source system rather than showing a
                        generic badge is what lets a biller tell the EMR's claims from
                        the RIS's once both are live. */}
                    <td className="wl-cell-source">
                      {claim.is_source_claim ? (
                        <span className="wl-source-badge wl-source-synced" title={`Synced from ${claim.source_system}`}>
                          {claim.source_system}
                        </span>
                      ) : (
                        <span className="wl-source-badge wl-source-manual" title="Created by a biller in this application">
                          Manual
                        </span>
                      )}
                    </td>
                    <td className="wl-cell-patient">{claim.patient_name}</td>
                    <td className="wl-cell-provider">
                      {/* A technical claim has no rendering provider (837P 2310B is
                          omitted); the ordering provider is the one it is filed under. */}
                      {claim.billing_component === 'technical' && claim.referring_provider && (
                        <span className="wl-provider-ref" title="Referring (ordering) provider">
                          <span className="wl-provider-ref-label">ref.</span> {claim.referring_provider}
                        </span>
                      )}
                      {claim.provider}
                      {claim.provider && !claim.rendering_provider_id && (
                        <button className="wl-provider-unlinked" title="Provider not linked — click to link" onClick={() => openLinkProviderModal(claim)}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                            <line x1="2" y1="2" x2="22" y2="22" />
                          </svg>
                        </button>
                      )}
                    </td>
                    <td>{parseLocalDate(claim.service_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>
                      {claim.cpt_codes.length > 0 ? (
                        <div className="wl-cpt-list">
                          {claim.cpt_codes.map((code) => (
                            <span key={code} className="wl-cpt-tag">{code}</span>
                          ))}
                        </div>
                      ) : claim.provisional_procedure_code ? (
                        <div className="wl-cpt-list">
                          <span
                            className="wl-cpt-tag wl-cpt-provisional"
                            title="Provisional code from the source system. A coder confirms or changes it."
                          >
                            {claim.provisional_procedure_code}
                          </span>
                          <span className="wl-cpt-provisional-hint">provisional</span>
                        </div>
                      ) : claim.note_preview ? (
                        <span className="wl-note-preview">{claim.note_preview}</span>
                      ) : (
                        <span className="wl-note-empty">—</span>
                      )}
                    </td>
                    <AmountCell claim={claim} />
                    <td>
                      <span className={`wl-status-badge ${statusConfig.className}`}>
                        {statusConfig.label}
                      </span>
                      {holds.length > 0 && (
                        <div className="wl-hold-chips">
                          {holds.map((hold) => (
                            <span key={hold.id} className="wl-hold-chip" title={`Held ${hold.age_days} day${hold.age_days === 1 ? '' : 's'} — work it on the Holds page`}>
                              {HOLD_REASON_LABELS[hold.reason] || hold.reason}
                              {hold.authority_name && <span className="wl-hold-chip-authority"> · {hold.authority_name}</span>}
                              <span className="wl-hold-chip-age">{hold.age_days}d</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="wl-cell-actions">
                      {claim.claim_status === 'ready' && (
                        <button className="wl-btn-generate" onClick={() => handleAction('generate', claim)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="12" y1="18" x2="12" y2="12" />
                            <line x1="9" y1="15" x2="15" y2="15" />
                          </svg>
                          Generate Claim
                        </button>
                      )}

                      {claim.claim_status === 'generated' && (
                        <button
                          className="wl-btn-send"
                          onClick={() => handleAction('review', claim)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                          Review Claim
                        </button>
                      )}

                      <div className="wl-more-wrapper">
                        <button
                          className="wl-btn-more"
                          onClick={(e) => {
                            if (actionMenuOpen === claim.id) {
                              setActionMenuOpen(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setMenuPosition({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                              setActionMenuOpen(claim.id);
                            }
                          }}
                          aria-label="More actions"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="5" r="1" />
                            <circle cx="12" cy="12" r="1" />
                            <circle cx="12" cy="19" r="1" />
                          </svg>
                        </button>

                        {actionMenuOpen === claim.id && (
                          <>
                            <div className="wl-menu-backdrop" onClick={() => setActionMenuOpen(null)} />
                            <div className="wl-action-menu" style={{ top: menuPosition.top, right: menuPosition.right }}>
                              {claim.claim_status !== 'ready' && (
                                <button onClick={() => handleAction('view', claim)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                  View Details
                                </button>
                              )}
                              {claim.claim_status === 'ready' && (
                                <button onClick={() => handleAction('edit-encounter', claim)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  Edit Encounter
                                </button>
                              )}
                              {(claim.claim_status === 'generated' || claim.claim_status === 'rejected') && (
                                <button onClick={() => handleAction('edit', claim)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  Edit Encounter
                                </button>
                              )}
                              {claim.claim_status === 'rejected' && (
                                <button onClick={() => handleAction('resubmit', claim)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="23 4 23 10 17 10" />
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                                  </svg>
                                  Resubmit
                                </button>
                              )}
                              {claim.claim_status !== 'voided' && (
                                <button className="wl-menu-danger" onClick={() => handleAction('void', claim)}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                  </svg>
                                  Void Claim
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Claim Modal */}
      {createModal && (
        <div className="wl-modal-backdrop">
          <div className="wl-create-modal">

            {/* Header */}
            <div className="wl-create-modal-header">
              <div className="wl-create-modal-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </div>
              <div className="wl-create-modal-header-text">
                <h2 className="wl-create-modal-title">Create Claim</h2>
                <p className="wl-create-modal-subtitle">Manually create a new encounter for {selectedFacility.name}</p>
              </div>
              <button className="wl-modal-close" onClick={() => { if (!creating) setCreateModal(false); }} disabled={creating}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Patient Section */}
            <div className="wl-modal-section">
              <p className="wl-modal-section-title">Patient</p>
              <div className="wl-mode-tabs">
                <button
                  className={`wl-mode-tab ${patientMode === 'existing' ? 'active' : ''}`}
                  onClick={() => { setPatientMode('existing'); setCreateForm((f) => ({...f, patientId: '', mrn: '', payerId: null, payerName: '', policyNumber: '', effectiveDate: '', isSelfPayPayer: false})); setPatientSearch(''); setCreatePayerSearch(''); }}
                  disabled={creating}
                >
                  Existing Patient
                </button>
                <button
                  className={`wl-mode-tab ${patientMode === 'new' ? 'active' : ''}`}
                  onClick={() => { setPatientMode('new'); setCreateForm((f) => ({...f, patientId: '', mrn: ''})); setSelectedPatientHasPayer(null); setPatientSearch(''); }}
                  disabled={creating}
                >
                  New Patient
                </button>
              </div>

              {patientMode === 'existing' ? (
                <div className="wl-patient-search-wrapper">
                  <svg className="wl-patient-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    className="wl-form-input wl-patient-search-input"
                    placeholder={patientsLoading ? 'Loading patients…' : 'Search by name or date of birth…'}
                    value={patientSearch}
                    onChange={(e) => { setPatientSearch(e.target.value); setCreateForm((f) => ({...f, patientId: '', mrn: ''})); setSelectedPatientHasPayer(null); }}
                    onFocus={() => setPatientSearchOpen(true)}
                    onBlur={() => setPatientSearchOpen(false)}
                    disabled={patientsLoading || creating}
                  />
                  {patientSearchOpen && (
                    <div className="wl-patient-dropdown">
                      {patientsLoading ? (
                        <div className="wl-patient-dropdown-msg">Loading patients…</div>
                      ) : filteredPatients.length === 0 ? (
                        <div className="wl-patient-dropdown-msg">
                          {patientSearch ? 'No patients match your search.' : 'No patients in this facility.'}
                        </div>
                      ) : (
                        filteredPatients.slice(0, 8).map((p) => (
                          <div
                            key={p.id}
                            className={`wl-patient-option ${createForm.patientId === p.id ? 'wl-patient-option-selected' : ''}`}
                            onMouseDown={() => {
                              setCreateForm((f) => ({...f, patientId: p.id, mrn: p.facility_mrn || ''}));
                              setPatientSearch(`${p.first_name} ${p.last_name}`);
                              setSelectedPatientHasPayer(!!p.insurance_provider);
                              setPatientSearchOpen(false);
                            }}
                          >
                            <span className="wl-patient-option-name">{p.first_name} {p.last_name}</span>
                            {p.date_of_birth && (
                              <span className="wl-patient-option-dob">{p.date_of_birth.split('T')[0]}</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="wl-form-grid">
                  <div className="wl-form-field">
                    <label className="wl-form-label">First Name <span className="wl-required">*</span></label>
                    <input className="wl-form-input" type="text" placeholder="First name" value={createForm.firstName} onChange={(e) => setCreateForm((f) => ({...f, firstName: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">Last Name <span className="wl-required">*</span></label>
                    <input className="wl-form-input" type="text" placeholder="Last name" value={createForm.lastName} onChange={(e) => setCreateForm((f) => ({...f, lastName: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">Date of Birth <span className="wl-required">*</span></label>
                    <input className="wl-form-input" type="date" value={createForm.dob} onChange={(e) => setCreateForm((f) => ({...f, dob: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">Sex</label>
                    <select className="wl-form-select" value={createForm.sex} onChange={(e) => setCreateForm((f) => ({...f, sex: e.target.value}))} disabled={creating}>
                      <option value="U">Unknown</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">Phone</label>
                    <input className="wl-form-input" type="tel" placeholder="Phone number" value={createForm.phone} onChange={(e) => setCreateForm((f) => ({...f, phone: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">Email</label>
                    <input className="wl-form-input" type="email" placeholder="Email address" value={createForm.email} onChange={(e) => setCreateForm((f) => ({...f, email: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field wl-form-field-full">
                    <label className="wl-form-label">Street Address</label>
                    <input className="wl-form-input" type="text" placeholder="Street address" value={createForm.streetAddress} onChange={(e) => setCreateForm((f) => ({...f, streetAddress: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">City</label>
                    <input className="wl-form-input" type="text" placeholder="City" value={createForm.city} onChange={(e) => setCreateForm((f) => ({...f, city: e.target.value}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">State</label>
                    <input className="wl-form-input" type="text" placeholder="ST" maxLength={2} value={createForm.state} onChange={(e) => setCreateForm((f) => ({...f, state: e.target.value.toUpperCase()}))} disabled={creating} />
                  </div>
                  <div className="wl-form-field">
                    <label className="wl-form-label">ZIP</label>
                    <input className="wl-form-input" type="text" placeholder="ZIP code" value={createForm.zip} onChange={(e) => setCreateForm((f) => ({...f, zip: e.target.value}))} disabled={creating} />
                  </div>
                </div>
              )}

              {/* MRN — shown for both new and existing patients; recorded against the selected facility */}
              {(patientMode === 'new' || createForm.patientId) && (
                <div className="wl-form-field wl-form-field-full" style={{ marginTop: 14 }}>
                  <label className="wl-form-label">MRN <span className="wl-form-hint">(Medical Record Number at {selectedFacility?.name})</span></label>
                  <input className="wl-form-input" type="text" placeholder="Facility medical record number" value={createForm.mrn} onChange={(e) => setCreateForm((f) => ({...f, mrn: e.target.value}))} disabled={creating} />
                  {patientMode === 'existing' && createForm.patientId && (() => {
                    const existingMrn = patientList.find((p) => p.id === createForm.patientId)?.facility_mrn;
                    return (
                      <span className="wl-form-hint" style={{ display: 'block', marginTop: 4 }}>
                        {existingMrn
                          ? 'This patient already has an MRN on file at this facility (shown above). Edit it to update, or leave it as-is.'
                          : 'No MRN on file for this patient at this facility yet. Leave blank to skip.'}
                      </span>
                    );
                  })()}
                </div>
              )}

              {patientMode === 'existing' && selectedPatientHasPayer === false && createForm.patientId && (
                <div className="wl-no-payer-callout">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <div className="wl-no-payer-callout-body">
                    <strong>No primary payer on file</strong>
                    <p>Add an insurance plan or designate this patient as Self Pay before creating a claim.</p>
                    <a href={`/patients/${createForm.patientId}`} target="_blank" rel="noreferrer" className="wl-no-payer-callout-link">
                      Open patient summary →
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Primary Payer Section — required for new patients */}
            {patientMode === 'new' && (
              <div className="wl-modal-section">
                <p className="wl-modal-section-title">Primary Payer <span className="wl-required">*</span></p>
                <div className="wl-form-grid">
                  <div className="wl-form-field wl-form-field-full">
                    <label className="wl-form-label">Payer <span className="wl-required">*</span></label>
                    {createForm.payerId ? (
                      <div className="wl-payer-selected-row">
                        <span className="wl-payer-selected-name">{createForm.payerName}</span>
                        <button
                          type="button"
                          className="wl-payer-selected-change"
                          onClick={() => {
                            setCreateForm((f) => ({ ...f, payerId: null, payerName: '', policyNumber: '', isSelfPayPayer: false }));
                            setCreatePayerSearch('');
                          }}
                          disabled={creating}
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className="wl-payer-combobox">
                        <input
                          className="wl-form-input"
                          type="text"
                          value={createPayerSearch}
                          placeholder="Search payers…"
                          autoComplete="off"
                          onFocus={() => setCreatePayerDropdownOpen(true)}
                          onChange={(e) => { setCreatePayerSearch(e.target.value); setCreatePayerDropdownOpen(true); }}
                          onBlur={() => setTimeout(() => setCreatePayerDropdownOpen(false), 150)}
                          disabled={creating}
                        />
                        {createPayerDropdownOpen && (
                          <div className="wl-payer-dropdown">
                            {(() => {
                              const selfPayPayer  = createPayersList.find((py) => py.is_self_pay_payer);
                              const regularPayers = createPayersList.filter((py) =>
                                !py.is_self_pay_payer &&
                                (!createPayerSearch.trim() || py.name.toLowerCase().includes(createPayerSearch.toLowerCase()))
                              );
                              const showSelfPay = selfPayPayer && (
                                !createPayerSearch.trim() ||
                                selfPayPayer.name.toLowerCase().includes(createPayerSearch.toLowerCase())
                              );
                              const selectPayer = (py) => {
                                setCreateForm((f) => ({
                                  ...f,
                                  payerId: py.payer_id,
                                  payerName: py.name,
                                  isSelfPayPayer: !!py.is_self_pay_payer,
                                  policyNumber: py.is_self_pay_payer ? '' : f.policyNumber,
                                }));
                                setCreatePayerSearch('');
                                setCreatePayerDropdownOpen(false);
                              };
                              return (
                                <>
                                  {showSelfPay && (
                                    <button
                                      key={selfPayPayer.payer_id}
                                      type="button"
                                      className="wl-payer-dropdown-item wl-payer-dropdown-selfpay"
                                      onMouseDown={() => selectPayer(selfPayPayer)}
                                    >
                                      {selfPayPayer.name}
                                      <span className="wl-payer-dropdown-code">Uninsured / Self Pay</span>
                                    </button>
                                  )}
                                  {showSelfPay && regularPayers.length > 0 && (
                                    <div className="wl-payer-dropdown-divider" />
                                  )}
                                  {regularPayers.map((py) => (
                                    <button
                                      key={py.payer_id}
                                      type="button"
                                      className="wl-payer-dropdown-item"
                                      onMouseDown={() => selectPayer(py)}
                                    >
                                      {py.name}
                                      {py.payer_code && <span className="wl-payer-dropdown-code">{py.payer_code}</span>}
                                    </button>
                                  ))}
                                  {!showSelfPay && regularPayers.length === 0 && (
                                    <div className="wl-payer-dropdown-empty">No payers match your search.</div>
                                  )}
                                  {createPayersList.length === 0 && (
                                    <div className="wl-payer-dropdown-empty">No payers on file.</div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {createForm.payerId && !createForm.isSelfPayPayer && (
                    <div className="wl-form-field wl-form-field-full">
                      <label className="wl-form-label">Member / Policy ID <span className="wl-required">*</span></label>
                      <input
                        className="wl-form-input"
                        type="text"
                        placeholder="Member ID or Policy #"
                        value={createForm.policyNumber}
                        onChange={(e) => setCreateForm((f) => ({...f, policyNumber: e.target.value}))}
                        disabled={creating}
                      />
                    </div>
                  )}
                  {createForm.payerId && (
                    <div className="wl-form-field wl-form-field-full">
                      <label className="wl-form-label">Effective Date <span className="wl-required">*</span></label>
                      <input
                        className="wl-form-input"
                        type="date"
                        value={createForm.effectiveDate}
                        onChange={(e) => setCreateForm((f) => ({...f, effectiveDate: e.target.value}))}
                        disabled={creating}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Encounter Details Section */}
            <div className="wl-modal-section">
              <p className="wl-modal-section-title">Encounter Details</p>
              <div className="wl-form-grid">
                <div className="wl-form-field">
                  <label className="wl-form-label">Service Date <span className="wl-required">*</span></label>
                  <input className="wl-form-input" type="date" value={createForm.serviceDate} onChange={(e) => setCreateForm((f) => ({...f, serviceDate: e.target.value}))} disabled={creating} />
                </div>
                <div className="wl-form-field">
                  <label className="wl-form-label">Form Type</label>
                  <span className="wl-form-static">
                    {claimFormLabel(createForm.formType, facilityComponent)}
                    <span className="wl-form-hint">· facility profile</span>
                  </span>
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">
                    Rendering Provider{!referringRequired && <> <span className="wl-required">*</span></>}
                    {referringRequired && <span className="wl-form-hint"> · not filed on technical claims</span>}
                  </label>
                  <ProviderTypeahead
                    className="wl-form-input"
                    value={createForm.renderingProvider}
                    onChange={(e) => setCreateForm((f) => ({...f, renderingProvider: e.target.value}))}
                    onSelect={(p) => setCreateForm((f) => ({...f, renderingProvider: p.name, renderingProviderNPI: p.npi || '', renderingProviderId: p.id || null}))}
                    providers={providerList}
                    placeholder="Last, First or First Last"
                    disabled={creating}
                  />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Rendering Provider NPI</label>
                  <input className="wl-form-input" type="text" placeholder="10-digit NPI" value={createForm.renderingProviderNPI} onChange={(e) => setCreateForm((f) => ({...f, renderingProviderNPI: e.target.value}))} disabled={creating} />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">
                    Referring Provider{referringRequired && <> <span className="wl-required">*</span></>}
                    {referringRequired && <span className="wl-form-hint"> · the physician who ordered the study</span>}
                  </label>
                  <ProviderTypeahead
                    className="wl-form-input"
                    value={createForm.referringProvider}
                    onChange={(e) => setCreateForm((f) => ({...f, referringProvider: e.target.value}))}
                    onSelect={(p) => setCreateForm((f) => ({...f, referringProvider: p.name, referringProviderNPI: p.npi || ''}))}
                    providers={providerList}
                    placeholder="Last, First or First Last"
                    disabled={creating}
                  />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Referring Provider NPI</label>
                  <input className="wl-form-input" type="text" placeholder="10-digit NPI" value={createForm.referringProviderNPI} onChange={(e) => setCreateForm((f) => ({...f, referringProviderNPI: e.target.value}))} disabled={creating} />
                </div>
              </div>
            </div>

            {createError && <div className="wl-modal-error">{createError}</div>}

            <div className="wl-void-modal-actions">
              <button className="wl-btn-cancel" onClick={() => setCreateModal(false)} disabled={creating}>
                Cancel
              </button>
              <button className="wl-btn-create-confirm" onClick={handleCreateClaim} disabled={creating || !createFormValid}>
                {creating ? (
                  <><span className="wl-spinner" />Creating…</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Create Claim
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Claim Modal */}
      {reviewModal && (
        <ReviewClaimModal
          claim={reviewModal}
          facility={selectedFacility}
          onClose={() => setReviewModal(null)}
          onSubmitted={() => { setReviewModal(null); loadWorklist(); }}
        />
      )}

      {/* Void Claim Modal */}
      {voidModal && (
        <div className="wl-modal-backdrop">
          <div className="wl-void-modal">
            <div className="wl-void-modal-header">
              <div className="wl-void-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
              </div>
              <div>
                <h2 className="wl-void-modal-title">Void Claim</h2>
                <p className="wl-void-modal-subtitle">This action cannot be undone. The claim will be permanently marked voided.</p>
              </div>
            </div>

            <div className="wl-void-modal-meta">
              <div className="wl-void-modal-meta-item">
                <span className="wl-void-modal-meta-label">Claim</span>
                <span className="wl-void-modal-meta-value">{voidModal.claim_number || '—'}</span>
              </div>
              <div className="wl-void-modal-meta-item">
                <span className="wl-void-modal-meta-label">Patient</span>
                <span className="wl-void-modal-meta-value">{voidModal.patient_name}</span>
              </div>
              <div className="wl-void-modal-meta-item">
                <span className="wl-void-modal-meta-label">Current Status</span>
                <span className="wl-void-modal-meta-value">{CLAIM_STATUS_CONFIG[voidModal.claim_status]?.label || voidModal.claim_status}</span>
              </div>
            </div>

            <label className="wl-void-modal-label" htmlFor="void-reason">
              Reason for voiding <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              id="void-reason"
              className="wl-void-modal-textarea"
              placeholder="Describe why this claim is being voided..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
              disabled={voiding}
            />

            <div className="wl-void-modal-actions">
              <button
                className="wl-btn-cancel"
                onClick={() => { setVoidModal(null); setVoidReason(''); }}
                disabled={voiding}
              >
                Cancel
              </button>
              <button
                className="wl-btn-void-confirm"
                onClick={handleVoidClaim}
                disabled={voiding || !voidReason.trim()}
              >
                {voiding ? (
                  <><span className="wl-spinner" />Voiding...</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </svg>
                    Void Claim
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {ediPreview && (
        <EdiPreviewModal
          files={ediPreview}
          onClose={() => setEdiPreview(null)}
        />
      )}

      {/* Link Provider Modal */}
      {linkProviderModal && (
        <div className="wl-modal-backdrop">
          <div className="wl-link-provider-modal">
            <div className="wl-create-modal-header">
              <div className="wl-create-modal-icon wl-link-provider-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </div>
              <div className="wl-create-modal-header-text">
                <h2 className="wl-create-modal-title">Link Provider</h2>
                <p className="wl-create-modal-subtitle">{linkProviderModal.patient_name} · {linkProviderModal.encounter_id}</p>
              </div>
              <button className="wl-modal-close" onClick={() => { if (!savingLinkProvider) setLinkProviderModal(null); }} disabled={savingLinkProvider}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="wl-modal-section">
              <p className="wl-modal-section-title">Provider Details</p>
              <div className="wl-form-grid">
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Rendering Provider <span className="wl-required">*</span></label>
                  <ProviderTypeahead
                    className="wl-form-input"
                    value={linkProviderForm.renderingProvider}
                    onChange={(e) => setLinkProviderForm((f) => ({...f, renderingProvider: e.target.value, renderingProviderId: null}))}
                    onSelect={(p) => setLinkProviderForm((f) => ({...f, renderingProvider: p.name, renderingProviderNPI: p.npi || '', renderingProviderId: p.id || null}))}
                    providers={providerList}
                    placeholder="Last, First or First Last"
                    disabled={savingLinkProvider}
                  />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Rendering Provider NPI</label>
                  <input className="wl-form-input" type="text" placeholder="10-digit NPI" value={linkProviderForm.renderingProviderNPI} onChange={(e) => setLinkProviderForm((f) => ({...f, renderingProviderNPI: e.target.value}))} disabled={savingLinkProvider} />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Referring Provider</label>
                  <ProviderTypeahead
                    className="wl-form-input"
                    value={linkProviderForm.referringProvider}
                    onChange={(e) => setLinkProviderForm((f) => ({...f, referringProvider: e.target.value}))}
                    onSelect={(p) => setLinkProviderForm((f) => ({...f, referringProvider: p.name, referringProviderNPI: p.npi || ''}))}
                    providers={providerList}
                    placeholder="Last, First or First Last"
                    disabled={savingLinkProvider}
                  />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Referring Provider NPI</label>
                  <input className="wl-form-input" type="text" placeholder="10-digit NPI" value={linkProviderForm.referringProviderNPI} onChange={(e) => setLinkProviderForm((f) => ({...f, referringProviderNPI: e.target.value}))} disabled={savingLinkProvider} />
                </div>
              </div>
            </div>

            {linkProviderError && <div className="wl-modal-error">{linkProviderError}</div>}

            <div className="wl-void-modal-actions">
              <button className="wl-btn-cancel" onClick={() => setLinkProviderModal(null)} disabled={savingLinkProvider}>
                Cancel
              </button>
              <button className="wl-btn-link-provider-confirm" onClick={handleSaveLinkProvider} disabled={savingLinkProvider}>
                {savingLinkProvider ? (
                  <><span className="wl-spinner" />Linking…</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    Link Provider
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Encounter Modal */}
      {editEncounterModal && (
        <div className="wl-modal-backdrop">
          <div className="wl-create-modal">
            <div className="wl-create-modal-header">
              <div className="wl-create-modal-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </div>
              <div className="wl-create-modal-header-text">
                <h2 className="wl-create-modal-title">Edit Encounter</h2>
                <p className="wl-create-modal-subtitle">{editEncounterModal.patient_name} · {editEncounterModal.encounter_id}</p>
              </div>
              <button className="wl-modal-close" onClick={() => { if (!savingEncounter) setEditEncounterModal(null); }} disabled={savingEncounter}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="wl-modal-section">
              <p className="wl-modal-section-title">Encounter Details</p>
              <div className="wl-form-grid">
                <div className="wl-form-field">
                  <label className="wl-form-label">Service Date <span className="wl-required">*</span></label>
                  <input className="wl-form-input" type="date" value={editEncounterForm.serviceDate} onChange={(e) => setEditEncounterForm((f) => ({...f, serviceDate: e.target.value}))} disabled={savingEncounter} />
                </div>
                <div className="wl-form-field">
                  <label className="wl-form-label">Form Type</label>
                  <span className="wl-form-static">
                    {claimFormLabel(editEncounterForm.formType, editEncounterForm.billingComponent)}
                    <span className="wl-form-hint">· facility profile</span>
                  </span>
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">
                    Rendering Provider{editEncounterForm.billingComponent !== 'technical' && <> <span className="wl-required">*</span></>}
                    {editEncounterForm.billingComponent === 'technical' && <span className="wl-form-hint"> · not filed on technical claims</span>}
                  </label>
                  <ProviderTypeahead
                    className="wl-form-input"
                    value={editEncounterForm.renderingProvider}
                    onChange={(e) => setEditEncounterForm((f) => ({...f, renderingProvider: e.target.value}))}
                    onSelect={(p) => setEditEncounterForm((f) => ({...f, renderingProvider: p.name, renderingProviderNPI: p.npi || '', renderingProviderId: p.id || null}))}
                    providers={providerList}
                    placeholder="Last, First or First Last"
                    disabled={savingEncounter}
                  />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Rendering Provider NPI</label>
                  <input className="wl-form-input" type="text" placeholder="10-digit NPI" value={editEncounterForm.renderingProviderNPI} onChange={(e) => setEditEncounterForm((f) => ({...f, renderingProviderNPI: e.target.value}))} disabled={savingEncounter} />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">
                    Referring Provider{editEncounterForm.billingComponent === 'technical' && <> <span className="wl-required">*</span></>}
                  </label>
                  <ProviderTypeahead
                    className="wl-form-input"
                    value={editEncounterForm.referringProvider}
                    onChange={(e) => setEditEncounterForm((f) => ({...f, referringProvider: e.target.value}))}
                    onSelect={(p) => setEditEncounterForm((f) => ({...f, referringProvider: p.name, referringProviderNPI: p.npi || ''}))}
                    providers={providerList}
                    placeholder="Last, First or First Last"
                    disabled={savingEncounter}
                  />
                </div>
                <div className="wl-form-field wl-form-field-full">
                  <label className="wl-form-label">Referring Provider NPI</label>
                  <input className="wl-form-input" type="text" placeholder="10-digit NPI" value={editEncounterForm.referringProviderNPI} onChange={(e) => setEditEncounterForm((f) => ({...f, referringProviderNPI: e.target.value}))} disabled={savingEncounter} />
                </div>
              </div>
            </div>

            {editEncounterError && <div className="wl-modal-error">{editEncounterError}</div>}

            <div className="wl-void-modal-actions">
              <button className="wl-btn-cancel" onClick={() => setEditEncounterModal(null)} disabled={savingEncounter}>
                Cancel
              </button>
              <button className="wl-btn-create-confirm" onClick={handleSaveEncounter} disabled={savingEncounter}>
                {savingEncounter ? (
                  <><span className="wl-spinner" />Saving…</>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Worklist;
